/**
 * 에러 진단 정보 조립
 *
 * @why 이 앱은 태블릿·휴대폰에서 주로 쓰이는데, 그 환경에는 개발자 도구가 없다.
 * console에 아무리 자세히 남겨도 사용자는 볼 수 없고, 우리는 "저장이 안 돼요"라는
 * 한 줄만 받게 된다. 그래서 원인 판별에 필요한 최소 사실을 화면에 띄울 수 있는
 * 문자열로 만들어 둔다. (실제 장애가 태블릿에서만 재현된 사건에서 나온 요구)
 *
 * 담는 값의 기준: "이 값이 달랐다면 다른 결론을 내렸을 것"인지.
 * - 에러 이름/메시지: 타임아웃인지 권한 오류인지 구분
 * - 단계(stage): 어느 요청에서 끊겼는지 (재검증 읽기 vs 저장 쓰기)
 * - 소요 시간: 타임아웃 상한에 걸린 것인지, 즉시 실패한 것인지
 * - 온라인 여부: 기기 자체가 끊긴 것인지 서버 쪽 문제인지
 * - UA: 브라우저·기기별로만 나는 문제인지
 */

/** 진단 정보에 함께 담을 상황 값 */
export type ErrorContext = Record<string, string | number | boolean | null | undefined>

/**
 * 사용자에게 보여줄 한 줄 메시지를 뽑는다.
 *
 * @why Error가 아닌 값(문자열, 객체)이 throw될 수 있고, 그때 `err.message`는
 * undefined가 되어 화면에 빈 칸이 뜬다. 어떤 값이 와도 문장이 나오게 한다.
 */
export function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error) return error
  return fallback
}

/**
 * 화면에 띄울 수 있는 진단 문자열을 만든다.
 *
 * @param error 실제로 throw된 값
 * @param context 발생 시점의 상황 (단계, 건수, 소요 시간 등)
 */
export function describeError(error: unknown, context: ErrorContext = {}): string {
  const lines: string[] = []

  if (error instanceof Error) {
    lines.push(`error: ${error.name}: ${error.message}`)
  } else {
    lines.push(`error: ${String(error)}`)
  }

  for (const [key, value] of Object.entries(context)) {
    if (value === undefined) continue
    lines.push(`${key}: ${String(value)}`)
  }

  // navigator는 SSR·테스트 환경에서 없을 수 있다
  if (typeof navigator !== 'undefined') {
    lines.push(`online: ${navigator.onLine}`)
    lines.push(`ua: ${navigator.userAgent}`)
  }

  lines.push(`at: ${new Date().toISOString()}`)

  return lines.join('\n')
}
